import { useProjectLimits } from '@api/limits';
import { LimitsExceededDialog } from '@components/GeneralDialogs';
import AddIcon from '@mui/icons-material/Add';
import { LoadingButton } from '@mui/lab';
import React, { useContext } from 'react';
import { ProjectContext } from 'routes/ProjectProvider';
import { LimitType } from 'types/limits';
import { ITenantCreationFields } from 'types/settings';
import { ITenant } from 'types/tenants';
import TenantCreateDialog from './TenantCreateDialog';

interface ITenantCreateButtonProps {
	onApprove: (values: ITenantCreationFields) => Promise<ITenant | undefined>;
}

const TenantCreateButton: React.FC<ITenantCreateButtonProps> = ({
	onApprove
}) => {
	const { currentProject } = useContext(ProjectContext);
	const { data, isLoading } = useProjectLimits(currentProject);
	const [open, setOpen] = React.useState(false);

	const handleClick = React.useCallback(() => {
		setOpen(!open);
	}, [open, setOpen]);

	const handleSubmit = React.useCallback(
		async (values?: ITenantCreationFields) => {
			const res = await onApprove(values!);
			setOpen(false);
			return res;
		},
		[setOpen, onApprove]
	);

	return (
		<>
			<LoadingButton
				variant="outlined"
				loading={isLoading}
				startIcon={<AddIcon />}
				size="small"
				data-testid="tenant-create-button"
				onClick={handleClick}
			>
				{intl.tr('tenants.table.action.add.button')}
			</LoadingButton>
			{open &&
				(data?.tenantExceeded ? (
					<LimitsExceededDialog
						limitType={LimitType.Tenants}
						onCancel={handleClick}
					/>
				) : (
					<TenantCreateDialog onSubmit={handleSubmit} onCancel={handleClick} />
				))}
		</>
	);
};

export default TenantCreateButton;
